import { useRef, useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Upload, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'

interface CarrierOption { id: string; name: string; code: string | null }

interface RateSheetRow {
  id: string
  original_filename: string
  carrier_name: string | null
  parse_format: string | null
  parse_status: string
  row_count: number
  effective_date: string | null
  expiry_date: string | null
  created_at: string
  show_url: string
}

interface Props {
  rate_sheets: RateSheetRow[]
  carriers: CarrierOption[]
  carrier_filter: string | null
}

const STATUS_STYLES: Record<string, string> = {
  parsed: 'bg-green-100 text-green-700',
  parsing: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-600',
  needs_review: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="secondary" size="sm" className={`rounded-full font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status.replace('_', ' ')}
    </Badge>
  )
}

function validity(row: RateSheetRow): string {
  if (!row.effective_date && !row.expiry_date) return '—'
  return `${row.effective_date || '…'} → ${row.expiry_date || '…'}`
}

export default function RateSheetsIndex({ rate_sheets }: Props) {
  const pathFor = useOrgPath()
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    setUploading(true)
    router.post(pathFor('/settings/rate_sheets'), formData, {
      onFinish: () => {
        setUploading(false)
        if (fileInput.current) fileInput.current.value = ''
      },
    })
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Rate Sheets</h1>
        <div>
          <input ref={fileInput} type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
          <Button size="sm" className="text-xs" disabled={uploading} onClick={() => fileInput.current?.click()}>
            {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
            Upload rate sheet
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">File</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[140px]">Carrier</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[120px]">Status</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[80px]">Rows</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground w-[200px]">Validity</th>
            </tr>
          </thead>
          <tbody>
            {rate_sheets.map((row) => (
              <tr key={row.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(row.show_url)}>
                <td className="px-6 py-3">
                  <Link href={row.show_url} className="text-[13px] font-medium text-foreground hover:underline">
                    {row.original_filename}
                  </Link>
                  {row.parse_format && <div className="text-[11px] text-muted-foreground">{row.parse_format}</div>}
                </td>
                <td className="px-6 py-3 text-[13px] text-foreground">{row.carrier_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                <td className="px-6 py-3"><StatusBadge status={row.parse_status} /></td>
                <td className="px-6 py-3 text-[13px] text-foreground">{row.row_count}</td>
                <td className="px-6 py-3 text-[12px] text-muted-foreground">{validity(row)}</td>
              </tr>
            ))}
            {rate_sheets.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No rate sheets yet. Upload an .xlsx to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

RateSheetsIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
